import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "@/lib/motion";

const HERO_BG =
  "https://images.pexels.com/photos/6727765/pexels-photo-6727765.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = () => document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-black">
      <motion.div style={{ y, scale }} className="absolute inset-0 pointer-events-none">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1600px] flex-col justify-end px-6 pb-20 pt-40 md:px-10"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-px w-12 bg-[#FF3B30]" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/70">
            Trade Shows · Brand Activations
          </span>
        </motion.div>

        <h1 className="font-display text-[15vw] font-bold uppercase leading-[0.85] tracking-tighter md:text-[11vw] lg:text-[10rem]">
          <MaskLine delay={0.35}>We build</MaskLine>
          <MaskLine delay={0.48} className="text-[#FF3B30]">
            visions
          </MaskLine>
          <MaskLine delay={0.61}>that sell.</MaskLine>
        </h1>

        <div className="mt-10 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-lg font-light leading-relaxed text-white/70"
          >
            A full-service exhibit house designing, fabricating and deploying
            unforgettable trade show environments and brand activations worldwide.
          </motion.p>

          <motion.button
            data-testid="hero-scroll-btn"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            onClick={go}
            className="group flex items-center gap-3 self-start font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 transition-colors group-hover:border-[#FF3B30] group-hover:bg-[#FF3B30]">
              <ArrowDown className="h-4 w-4" />
            </span>
            Scroll to explore
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
}
